/**
 * LocalStorage-backed mock for the MYB-121 basket configuration API.
 *
 * Used until the real Content Hub endpoints (save-basket / update-basket /
 * load-basket / basket-configurations) are implemented. Every access is logged
 * to the console with the `[basketConfig:mock]` prefix.
 */

const STORAGE_KEY = 'mybed.basketConfigurations';

function log(action, data) {
    console.log(`[basketConfig:mock] ${action}`, data ?? '');
}

export function generateBasketConfigId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `bc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readAll() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (err) {
        console.error('[basketConfig:mock] failed to read storage', err);
        return {};
    }
}

function writeAll(configs) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
    } catch (err) {
        console.error('[basketConfig:mock] failed to write storage', err);
    }
}

export function mockSaveBasketConfiguration(payload = {}) {
    log('save-basket', payload);
    const configs = readAll();
    const id = generateBasketConfigId();
    const now = new Date().toISOString();
    const record = {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now,
    };
    configs[id] = record;
    writeAll(configs);
    return { status: true, id, basketConfiguration: record };
}

export function mockUpdateBasketConfiguration(payload = {}) {
    log('update-basket', payload);
    const { id, previousBasketItems, ...data } = payload;
    if (!id) {
        throw new Error('basketConfiguration id is required');
    }
    const configs = readAll();
    const existing = configs[id] || {};
    const record = {
        ...existing,
        ...data,
        id,
        createdAt: existing.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
    configs[id] = record;
    writeAll(configs);
    return { status: true, id, basketItems: record.basketItems || [], basketConfiguration: record };
}

export function mockGetBasketConfiguration(id) {
    log('load-basket', id);
    const configs = readAll();
    const record = configs[id];
    if (!record) {
        return { status: false, message: 'Basket configuration not found', id };
    }
    return { status: true, ...record };
}

export function mockGetBasketConfigurations({ basketItemIds = [] } = {}) {
    log('basket-configurations', { basketItemIds });
    const wanted = new Set((basketItemIds || []).map((id) => String(id)));
    const configs = Object.values(readAll());
    const filtered = wanted.size === 0
        ? configs
        : configs.filter((config) => (config.basketItems || []).some((item) => wanted.has(String(item.id))));
    return { status: true, basketConfigurations: filtered };
}

export function mockDeleteBasketConfiguration(id) {
    log('delete-basket', id);
    const configs = readAll();
    const deleted = Boolean(configs[id]);
    delete configs[id];
    writeAll(configs);
    return { status: true, id, deleted };
}
